import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TablePagination, Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, PictureAsPdf, CalendarMonth, ContentCopy } from '@mui/icons-material';
import DataTable, { Column } from '../components/DataTable';
import EstimateSelectModal from '../components/EstimateSelectModal';
import { formatDate, formatCurrency } from '../lib/format';

interface WorkOrder {
  id: number;
  doc_no: string;
  customer_name: string;
  customer_company?: string;
  project_name: string;
  status: string;
  issue_date: string | null;
  order_date: string | null;
  start_date: string | null;
  end_date: string | null;
  subtotal: number;
  total_amount: number;
  created_at: string;
}

const statusMap: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' }> = {
  draft: { label: '下書き', color: 'default' },
  confirmed: { label: '確定', color: 'primary' },
  completed: { label: '完了', color: 'success' },
  cancelled: { label: 'キャンセル', color: 'error' },
};

export default function OrderPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<WorkOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [estimateModalOpen, setEstimateModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const result = await window.api.invoke('work_order:list', {
        search: search || undefined,
        status: statusFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        page: page + 1,
        limit,
      });
      setOrders(result.data);
      setTotal(result.total);
    } catch (err) {
      console.error('注文一覧取得エラー:', err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, dateFrom, dateTo, page, limit]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleDelete = async (id: number) => {
    try {
      await window.api.invoke('work_order:delete', id);
      setDeleteConfirm(null);
      loadOrders();
    } catch (err) {
      console.error('削除エラー:', err);
    }
  };

  const handleDuplicate = async (id: number) => {
    try {
      await window.api.invoke('work_order:duplicate', id);
      loadOrders();
    } catch (err) {
      console.error('複製エラー:', err);
    }
  };

  const handlePdfOutput = async (id: number) => {
    try {
      const result = await window.api.invoke('dialog:savePdf', {
        title: '注文請書PDF保存',
        defaultPath: `order_${id}.pdf`,
      });
      if (result.canceled) return;
      await window.api.invoke('pdf:generateOrder', id, result.filePath);
      alert('PDFを出力しました');
    } catch (err) {
      console.error('PDF出力エラー:', err);
    }
  };

  const handleCreateFromEstimate = () => {
    setEstimateModalOpen(true);
  };

  const handleEstimateSelected = async (estimateId: number) => {
    setEstimateModalOpen(false);
    navigate(`/order/edit/new?fromEstimate=${estimateId}`);
  };

  const columns: Column<WorkOrder>[] = [
    { id: 'doc_no', label: '番号', width: 120 },
    { id: 'customer_name', label: '顧客', width: 150, render: (row) => row.customer_company || row.customer_name },
    { id: 'project_name', label: '件名', width: 200 },
    {
      id: 'status', label: '状態', width: 100,
      render: (row) => {
        const s = statusMap[row.status] || { label: row.status, color: 'default' as const };
        return <Chip label={s.label} color={s.color} size="small" />;
      },
    },
    { id: 'issue_date', label: '発行日', width: 100, render: (row) => formatDate(row.issue_date) },
    { id: 'total_amount', label: '合計金額', width: 120, align: 'right', render: (row) => formatCurrency(row.total_amount) },
    {
      id: 'actions', label: '操作', width: 180, align: 'center',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip title="編集">
            <IconButton size="small" onClick={() => navigate(`/order/edit/${row.id}`)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="工程表">
            <IconButton size="small" onClick={() => navigate(`/schedule/${row.id}`)}>
              <CalendarMonth fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="PDF出力">
            <IconButton size="small" onClick={() => handlePdfOutput(row.id)}>
              <PictureAsPdf fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="複製">
            <IconButton size="small" onClick={() => handleDuplicate(row.id)}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="削除">
            <IconButton size="small" color="error" onClick={() => setDeleteConfirm(row.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">注文請書一覧</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Add />} onClick={() => navigate('/order/edit/new')}>
            新規作成
          </Button>
          <Button variant="contained" color="primary" onClick={handleCreateFromEstimate}>
            見積から作成
          </Button>
        </Box>
      </Box>

      {/* 検索フィルター */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="検索"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>状態</InputLabel>
            <Select value={statusFilter} label="状態" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">すべて</MenuItem>
              <MenuItem value="draft">下書き</MenuItem>
              <MenuItem value="confirmed">確定</MenuItem>
              <MenuItem value="completed">完了</MenuItem>
              <MenuItem value="cancelled">キャンセル</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="発行日(From)"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="発行日(To)"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="text" onClick={() => { setSearch(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); }}>
            クリア
          </Button>
        </Box>
      </Paper>

      {/* テーブル */}
      <Paper>
        <DataTable
          columns={columns}
          data={orders}
          loading={loading}
          emptyMessage="注文請書がありません"
        />
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
          labelRowsPerPage="表示件数"
        />
      </Paper>

      {/* 見積選択モーダル */}
      <EstimateSelectModal
        open={estimateModalOpen}
        onClose={() => setEstimateModalOpen(false)}
        onSelect={handleEstimateSelected}
      />

      {/* 削除確認 */}
      <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>この注文請書を削除してもよろしいですか？</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>キャンセル</Button>
          <Button color="error" onClick={() => deleteConfirm && handleDelete(deleteConfirm)}>削除</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
